import React, { useState, useEffect } from 'react';
import {
  HiOutlineArrowDownTray,
  HiOutlineArrowUpTray,
  HiOutlineDocumentArrowDown,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineInformationCircle,
} from 'react-icons/hi2';
import { excelImportService, authService } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import './MasterDataImport.css';

const MasterDataImport = () => {
  const { hasPermission } = usePermissions();
  const tenantId = authService.getTenantId();
  
  // Permission check
  const canImport = hasPermission('ExcelImport.Import');
  
  const [supportedTypes, setSupportedTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (tenantId && canImport) {
      loadSupportedTypes();
    } else if (!canImport) {
      setError('You do not have permission to import Excel data');
    } else {
      setError('Please login to import data');
    }
  }, [tenantId, canImport]);

  const loadSupportedTypes = async () => {
    try {
      const types = await excelImportService.getSupportedTypes();
      setSupportedTypes(types);
      if (types.length > 0) {
        setSelectedType(types[0]);
      }
    } catch (err) {
      setError('Failed to load supported types. Please try again.');
      console.error('Error loading supported types:', err);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!canImport) {
      setError('You do not have permission to import Excel data');
      return;
    }
    
    if (!selectedType) {
      setError('Please select a master data type');
      return;
    }

    try {
      setDownloading(true);
      setError('');
      await excelImportService.downloadTemplate(selectedType);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download template. Please try again.');
      console.error('Error downloading template:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!allowedExtensions.includes(fileExtension)) {
        setError('Invalid file type. Only .xlsx and .xls files are allowed.');
        setSelectedFile(null);
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size exceeds 10MB limit.');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError('');
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!canImport) {
      setError('You do not have permission to import Excel data');
      return;
    }
    
    if (!selectedType) {
      setError('Please select a master data type');
      return;
    }

    if (!selectedFile) {
      setError('Please select a file to import');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setImportResult(null);

      const result = await excelImportService.importFromExcel(selectedType, selectedFile);
      setImportResult(result);

      if (result.success) {
        // Clear file selection on success
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('file-input');
        if (fileInput) {
          fileInput.value = '';
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Failed to import file. Please try again.';
      setError(errorMessage);
      console.error('Error importing file:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!canImport) {
    return (
      <div className="master-data-import">
        <div className="error-message" style={{ 
          textAlign: 'center', 
          padding: '2rem',
          fontSize: '1.1rem',
          color: '#d93025'
        }}>
          <h2>Access Denied</h2>
          <p>You do not have permission to import Excel data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-data-import">
      <div className="import-header">
        <h2>Master Data Import</h2>
        <p>Import master data from Excel files</p>
      </div>

      <div className="import-container">
        {/* Step 1: Select Master Data Type */}
        <div className="import-section">
          <h3>
            <span className="step-number">1</span>
            Select Master Data Type
          </h3>
          <div className="form-group">
            <label htmlFor="masterDataType">Master Data Type</label>
            <select
              id="masterDataType"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setSelectedFile(null);
                setImportResult(null);
                setError('');
              }}
              className="form-select"
              disabled={!canImport}
            >
              <option value="">-- Select Type --</option>
              {supportedTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Step 2: Download Template */}
        <div className="import-section">
          <h3>
            <span className="step-number">2</span>
            Download Template
          </h3>
          <p className="section-description">
            Download the Excel template for the selected master data type. Fill in your data
            following the template format.
          </p>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            disabled={!selectedType || downloading || !canImport}
            className="btn btn-primary btn-download"
          >
            {downloading ? (
              <>
                <HiOutlineArrowDownTray className="icon" />
                Downloading...
              </>
            ) : (
              <>
                <HiOutlineDocumentArrowDown className="icon" />
                Download Template
              </>
            )}
          </button>
        </div>

        {/* Step 3: Upload File */}
        <div className="import-section">
          <h3>
            <span className="step-number">3</span>
            Upload Excel File
          </h3>
          <p className="section-description">
            Select the Excel file containing your master data to import.
          </p>
          <div className="file-upload-area">
            <input
              type="file"
              id="file-input"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="file-input"
              disabled={!canImport}
            />
            <label htmlFor="file-input" className="file-label">
              <HiOutlineArrowUpTray className="icon" />
              {selectedFile ? selectedFile.name : 'Choose Excel File'}
            </label>
            {selectedFile && (
              <div className="file-info">
                <span className="file-name">{selectedFile.name}</span>
                <span className="file-size">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Step 4: Import */}
        <div className="import-section">
          <h3>
            <span className="step-number">4</span>
            Import Data
          </h3>
          <button
            type="button"
            onClick={handleImport}
            disabled={!selectedType || !selectedFile || loading || !canImport}
            className="btn btn-primary btn-import"
          >
            {loading ? (
              <>
                <HiOutlineArrowUpTray className="icon" />
                Importing...
              </>
            ) : (
              <>
                <HiOutlineArrowUpTray className="icon" />
                Import Data
              </>
            )}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error">
            <HiOutlineXCircle className="icon" />
            {error}
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
            <div className="result-header">
              {importResult.success ? (
                <HiOutlineCheckCircle className="icon success-icon" />
              ) : (
                <HiOutlineXCircle className="icon error-icon" />
              )}
              <h4>{importResult.success ? 'Import Successful' : 'Import Completed with Errors'}</h4>
            </div>
            <div className="result-details">
              <div className="result-stat">
                <span className="stat-label">Total Rows:</span>
                <span className="stat-value">{importResult.totalRows}</span>
              </div>
              <div className="result-stat">
                <span className="stat-label">Successfully Imported:</span>
                <span className="stat-value success">{importResult.successCount}</span>
              </div>
              {importResult.errorCount > 0 && (
                <div className="result-stat">
                  <span className="stat-label">Errors:</span>
                  <span className="stat-value error">{importResult.errorCount}</span>
                </div>
              )}
            </div>
            {importResult.message && (
              <p className="result-message">{importResult.message}</p>
            )}
            {importResult.errors && importResult.errors.length > 0 && (
              <div className="error-list">
                <h5>Import Errors:</h5>
                <ul>
                  {importResult.errors.map((err, index) => (
                    <li key={index}>
                      Row {err.rowNumber}: {err.field} - {err.errorMessage}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="import-instructions">
          <h4>
            <HiOutlineInformationCircle className="icon" />
            Instructions
          </h4>
          <ul>
            <li>Select the master data type you want to import</li>
            <li>Download the template file for that type</li>
            <li>Fill in the template with your data (keep the header row)</li>
            <li>Upload the filled template file</li>
            <li>Review the import results</li>
          </ul>
          <div className="notes">
            <strong>Note:</strong> Only .xlsx and .xls files up to 10MB are supported. Make sure
            your data follows the template format exactly.
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterDataImport;

